import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import type { Participant } from "@shared/schema";

interface ParticipantFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  participant?: Participant;
  organisationId?: number;
}

export default function ParticipantFormModal({
  isOpen,
  onClose,
  participant,
  organisationId,
}: ParticipantFormModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEditing = !!participant;

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    instagram: "",
    lookingForBand: false,
  });

  // Reset form when modal opens/closes or participant changes
  useEffect(() => {
    if (isOpen) {
      if (participant) {
        setFormData({
          name: participant.name || "",
          email: participant.email || "",
          phone: participant.phone || "",
          instagram: participant.instagram || "",
          lookingForBand: participant.lookingForBand || false,
        });
      } else {
        setFormData({
          name: "",
          email: "",
          phone: "",
          instagram: "",
          lookingForBand: false,
        });
      }
    }
  }, [isOpen, participant]);

  // Create participant mutation
  const createParticipantMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch("/api/participants", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          organisationId,
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to create participant");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/participants`] });
      toast({
        title: "Success",
        description: "Participant created successfully",
      });
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update participant mutation
  const updateParticipantMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch(`/api/participants/${participant?.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to update participant");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/participants`] });
      toast({
        title: "Success",
        description: "Participant updated successfully",
      });
      onClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "Name is required",
        variant: "destructive",
      });
      return;
    }

    // Clean up Instagram handle
    const cleanedData = {
      ...formData,
      instagram: formData.instagram ? formData.instagram.replace(/^@/, "") : "",
    };

    if (isEditing) {
      updateParticipantMutation.mutate(cleanedData);
    } else {
      createParticipantMutation.mutate(cleanedData);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Participant" : "Add New Participant"}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter participant name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+1 (555) 000-0000"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="instagram">Instagram</Label>
            <Input
              id="instagram"
              value={formData.instagram}
              onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
              placeholder="@username"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="lookingForBand">Looking for Band</Label>
            <Switch
              id="lookingForBand"
              checked={formData.lookingForBand}
              onCheckedChange={(checked) => 
                setFormData({ ...formData, lookingForBand: checked })
              }
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={createParticipantMutation.isPending || updateParticipantMutation.isPending}
            >
              {createParticipantMutation.isPending || updateParticipantMutation.isPending
                ? "Saving..."
                : isEditing
                ? "Update"
                : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}